/**
 * Plan limits shared between web and functions.
 *
 * Single source of truth for free-tier caps. Both the client UI and the
 * Cloud Function billing webhook import from here so a limit change is a
 * one-line edit.
 */
import {} from "./schemas/guest";
import { countInvitationHeads } from "./guest-list";
/** Total attendees (host + plus-one + children, summed across guests) a free wedding may hold. */
export const FREE_GUEST_LIMIT = 30;
/** Daily AI Planner message cap per plan. UTC day window. */
export const CHAT_QUOTA = { free: 3, premium: 50 };
/**
 * Days of Premium access granted after the wedding date when the user
 * buys the one-off "Pakiet ślubny" instead of subscribing. Covers the
 * post-wedding tasks (thank-you notes, photo gallery sharing, RSVP
 * post-mortem) without locking the couple into a recurring charge.
 */
export const WEDDING_PASS_TAIL_DAYS = 90;
export const isPremium = (plan) => plan === "premium";
/**
 * True when the user should receive Premium entitlements right now.
 * Honors `planValidUntil` for wedding-pass and lapsed subscriptions;
 * active Mollie subscriptions stay Premium until cancel + webhook.
 */
export function isPremiumActive(user) {
    if (user.plan !== "premium")
        return false;
    if (user.mollieSubscriptionId)
        return true;
    if (!user.planValidUntil)
        return true;
    return Date.parse(user.planValidUntil) > Date.now();
}
export function chatQuotaFor(plan) {
    return CHAT_QUOTA[plan];
}
/** Daily AI Planner cap for the shared demo account (overrides premium quota). */
export const DEMO_CHAT_QUOTA = 3;
export function chatMessageCap(input) {
    if (input.isDemo)
        return DEMO_CHAT_QUOTA;
    return chatQuotaFor(input.plan);
}
/** "YYYY-MM-DD" key for the dailyUsage subcollection doc id. */
export function utcDayKey(d = new Date()) {
    return d.toISOString().slice(0, 10);
}
/**
 * Total invited people for plan-limit checks. Matches the guest list
 * "Osoby" stat — deduped invitation units, not raw Firestore doc count.
 */
export function countAttendees(guests) {
    return countInvitationHeads(guests.filter((g) => !g.deletedAt));
}
/** Returns true if adding `incoming` attendees would push a free user past the cap. */
export function wouldExceedFreeLimit(user, current, incoming) {
    return !isPremiumActive(user) && current + incoming > FREE_GUEST_LIMIT;
}
/** Per-photo upload cap for the QR guest gallery (image bytes after compression). */
export const GUEST_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
/** Total approved+pending photo cap per wedding for the QR guest gallery. */
export const GUEST_PHOTO_MAX_COUNT = 500;
/** Allowed MIME types for guest-uploaded photos — drives both client + server validation. */
export const GUEST_PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
export function isAllowedGuestPhotoMime(mime) {
    return GUEST_PHOTO_MIME_TYPES.includes(mime);
}
